import IconButton from 'Components/Buttons/IconButton';
import Drawer from 'Components/Drawer';
import Panel from 'Components/Panel';
import PanelDivider from 'Components/PanelDivider';
import Table from 'Components/SimpleTable';
import { actions as logActions } from 'ducks/logs';
import { actions as measurementActions } from 'ducks/measurements';
import ShowReading from 'Pages/Readings/Show';
import MeasurementData from 'Pages/Tree/Device/MeasurementData.js';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectMeasurementsByDevice, selectWiseButtonsForDevice } from 'selectors/measurements';
import { selectReadingsForDevice } from 'selectors/readings';
import sortMeasurements from 'utils/sortMeasurements';
import icons from 'utils/ui/icons';
import EditMeasurement from './Edit';
import ManageConditions from './ManageConditions';
import NewMeasurement from './New';
import ShowMeasurement from './Show';

const Component = props => {
    const automationConditions = useSelector(state => state.automationConditions.models);
    const device = useSelector(state => state.devices.models[props.deviceId]);

    const selectedMeasurementsByDevice = useMemo(() => selectMeasurementsByDevice(props.deviceId), [props.deviceId]);
    const measurements = useSelector(selectedMeasurementsByDevice);

    const selectedReadingsForDevice = useMemo(() => selectReadingsForDevice(device), [device]);
    const readings = useSelector(selectedReadingsForDevice);

    const relays = useSelector(state => state.logs.relays);
    const rootDevice = useSelector(state => state.devices.models[device.root_device_id]);
    const submitting = useSelector(state => state.measurements.submitting || state.indicators.submitting || state.logs.loading);

    const wiseButtonsSelector = useMemo(
        () => selectWiseButtonsForDevice(props.deviceId),
        [props.deviceId]
    );
    const wiseButtons = useSelector(wiseButtonsSelector);
    const isNewWiseBox = rootDevice.new_wise_box;

    const dispatch = useDispatch();
    const { remove, toggleRelay } = {
        remove: measurementActions.useRemove(),
        toggleRelay: (id, state) => dispatch(logActions.toggleRelay(id, state))
    };
    useEffect(() => {
        if (isNewWiseBox) {
            dispatch(logActions.fetchRelayStatus(device.id));
        }
    }, [device.id, dispatch, isNewWiseBox]);

    const [currentDrawer, setDrawer] = useState('');
    const [entityId, setEntityId] = useState(null);

    const onClose = () => setDrawer('');
    const onClick = (entityId, drawer) => event => {
        if (event && event.preventDefault) {
            event.preventDefault();
        }
        setEntityId(entityId);
        setDrawer(drawer);
    };

    const
        availableColumns = [
            { label: 'Reading ID', dataKey: 'id' },
            { label: 'Reading', dataKey: 'readingName' },
            { label: 'Actions', dataKey: 'actions' }
        ],
        usedColumns = [
            { label: 'Data', dataKey: 'data' },
            { label: 'ID', dataKey: 'id' },
            { label: 'Reading', dataKey: 'readingName' },
            { label: 'Name', dataKey: 'measurementName' },
            { label: 'Circuit Name', dataKey: 'circuitName' },
            { label: 'Actions', dataKey: 'actions' }
        ];

    if (isNewWiseBox) {
        usedColumns.splice(4, 0, { label: 'Condition?', dataKey: 'hasAutomationCondition' });
        usedColumns.splice(5, 0, { label: 'WiseButton', dataKey: 'wiseButton' });
    }

    const availableRows = [], usedRows = [];

    readings.forEach(reading => {
        let row = {};
        const measurement = measurements.find(measurement => measurement.reading_id === reading.id) || {};

        row.measurementName = measurement.name;
        row.circuitName = measurement.circuit_name;
        row.id = measurement.id || reading.id;
        row.inputName = measurement.input_name;
        row.name = measurement.name || reading.name;
        row.readingName = (
            <a href="#" onClick={onClick(reading.id, 'showReading')}>
                {reading.name}
            </a>
        );

        if (measurement.id) {
            const on = relays[measurement.id] === 'on';

            if (isNewWiseBox) {
                row.hasAutomationCondition = Object.values(automationConditions).find(automationCondition => automationCondition.measurement_id === measurement.id) ? 'Yes' : 'No';
                row.wiseButton = wiseButtons.find(wiseButton => wiseButton.id === measurement.wise_button_id)?.name || '-';
            }

            let Actuator;
            if (isNewWiseBox) {
                Actuator = (
                    <IconButton
                        disabled={submitting}
                        icon={icons[on ? 'off' : 'on']}
                        onClick={() => toggleRelay(measurement.id, on ? 'off' : 'on')}
                        submitting={submitting}
                        tooltip={`Toggle ${on ? 'Off' : 'On'}`}
                    />
                );
            }

            let ManageConditionsButton;
            if (isNewWiseBox) {
                ManageConditionsButton = <IconButton icon={icons.conditions} onClick={onClick(measurement.id, 'manageConditions')} tooltip="Manage Conditions"/>;
            }

            row.actions = (
                <React.Fragment>
                    <IconButton icon={icons.details} onClick={onClick(measurement.id, 'showMeasurement')} tooltip="Show measurement"/>
                    <IconButton icon={icons.edit} onClick={onClick(measurement.id, 'editMeasurement')} tooltip="Edit measurement"/>
                    {ManageConditionsButton}
                    <IconButton
                        confirmationText="Are you sure you want to remove this measurement?"
                        disabled={!!measurement.circuit_id}
                        icon={icons.destroy}
                        onClick={() => remove(measurement.id, () => {})}
                        submitting={submitting}
                        tooltip="Remove measurement"
                    />
                    {Actuator}
                </React.Fragment>
            );

            row.data = (
                <IconButton icon={icons.table} onClick={onClick(measurement.id, 'showData')} tooltip="Measurement Data"/>
            );

            usedRows.push(row);
        } else {
            row.actions = (
                <IconButton icon={icons.add} onClick={onClick(reading.id, 'newMeasurement')} tooltip="Add measurement"/>
            );

            availableRows.push(row);
        }
    });

    let drawer;
    switch (currentDrawer) {
        case 'editMeasurement':
            drawer = <EditMeasurement closeDrawer={onClose} device={device} id={entityId}/>;
            break;
        case 'manageConditions':
            drawer = <ManageConditions closeDrawer={onClose} measurementId={entityId}/>;
            break;
        case 'newMeasurement':
            drawer = <NewMeasurement closeDrawer={onClose} device={device} readingId={entityId}/>;
            break;
        case 'showData':
            drawer = <MeasurementData id={entityId}/>;
            break;
        case 'showMeasurement':
            drawer = <ShowMeasurement closeDrawer={onClose} id={entityId}/>;
            break;
        case 'showReading':
            drawer = <ShowReading disableButtons={true} id={entityId}/>;
            break;
    }

    let Used;
    if (usedRows.length) {
        Used = (
            <Panel header="Used Measurements">
                <Table columns={usedColumns} rows={usedRows.sort(sortMeasurements)}/>
            </Panel>
        );
    }

    let Available;
    if (availableRows.length) {
        Available = (
            <Panel header="Available Measurements">
                <Table columns={availableColumns} rows={availableRows.sort(sortMeasurements)}/>
            </Panel>
        );
    }

    let Divider;
    if (Available && Used) {
        Divider = (
            <PanelDivider/>
        );
    }

    return (
        <React.Fragment>
            <Drawer big={currentDrawer === 'showIndicator'} onClose={onClose} open={!!currentDrawer}>
                {drawer}
            </Drawer>
            {Used}
            {Divider}
            {Available}
        </React.Fragment>
    );
};

export default Component;
